/**
 * LiveJobButton — reusable button that dispatches a long-running server job
 * and renders live progress inside itself.
 *
 * Works with any endpoint pair that returns {job_id, status} on dispatch and
 * exposes a polled status endpoint. Pass shape-specific getters so we can
 * support multiple backends (background_jobs vs sync_jobs schemas).
 *
 * Props:
 *   - dispatchEndpoint: string (POST)
 *   - dispatchPayload: object (default: {})
 *   - statusEndpoint: (jobId) => string (GET)
 *   - rehydrateEndpoint: string | null (GET — optional, to resume on mount)
 *   - rehydratePickJob: (response) => jobDoc | null (default: first item)
 *   - getProgress: (job) => { processed, updated, failed, total }
 *                  total may be undefined → indeterminate bar
 *   - labels: { idle, running?(progress), done?(job), failed?(job) }
 *   - icons: { idle, running, done, failed }  (React nodes)
 *   - variant, className — passed to underlying Button
 *   - onComplete: (job) => void   — caller-supplied side effects
 *   - disabled, token: standard
 *   - confirmMessage: string — optional confirm before dispatch
 *
 * Terminal statuses recognized: completed, partial, partial_success, failed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const TERMINAL = new Set(['completed', 'partial', 'partial_success', 'failed']);
const SUCCESS_TERMINAL = new Set(['completed', 'partial', 'partial_success']);
const POLL_MS = 5000;
// 90-min safety stop. Sized for the real-world worst case observed on
// May 29 2026: a Crestview ``sync_everything`` took 78 min end-to-end,
// and ``authorization_sync`` over 38 clients commonly runs 60-80 min.
// The previous 15-min cap froze the UI on a stale snapshot while the
// backend kept advancing.
const MAX_POLL_MS = 90 * 60 * 1000;
// Tolerate transient network/auth hiccups (single 401 during silent
// JWT refresh, brief 502 from k8s ingress) without killing the poller.
// After this many CONSECUTIVE errors we give up — anything less and one
// bad tick orphans the UI while the backend job is still running fine.
const POLL_ERROR_LIMIT = 3;

export default function LiveJobButton({
  apiCall,
  token,
  dispatchEndpoint,
  dispatchPayload = {},
  statusEndpoint,
  rehydrateEndpoint = null,
  rehydratePickJob = (res) => {
    const list = res?.data?.data || res?.data || [];
    return Array.isArray(list) ? list[0] : (list?.jobs && list.jobs[0]) || null;
  },
  getProgress = (job) => ({
    processed: job?.items_processed || 0,
    updated: job?.items_updated || 0,
    failed: job?.items_failed || 0,
    total: job?.total_clients,
  }),
  labels = {},
  icons = {},
  onComplete,
  onError,
  confirmMessage = null,
  disabled: parentDisabled = false,
  variant = 'outline',
  className = '',
  progressBarClassName = 'bg-green-600',
  progressTrackClassName = 'bg-green-100',
  'data-testid': dataTestId,
}) {
  const [progress, setProgress] = useState(null); // {status, processed, updated, failed, total}
  const [dispatching, setDispatching] = useState(false);
  const pollRef = useRef(null);
  const safetyRef = useRef(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (safetyRef.current) { clearTimeout(safetyRef.current); safetyRef.current = null; }
  }, []);

  const poll = useCallback((jobId) => {
    stopPolling();
    let consecutiveErrors = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiCall(statusEndpoint(jobId), 'GET', null, token);
        const job = res?.data?.data || res?.data || res;
        if (!job || !mountedRef.current) return;
        // ``apiCall`` returns ``{success:false, error}`` on failure rather
        // than throwing — treat a missing status the same as a tick error
        // so a 401 wave doesn't get silently swallowed as "still running".
        if (res?.success === false || !job.status) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= POLL_ERROR_LIMIT) {
            console.warn(`LiveJobButton: ${POLL_ERROR_LIMIT} consecutive empty/failed polls for ${statusEndpoint(jobId)}; stopping.`);
            stopPolling();
            if (mountedRef.current) setProgress(null);
          }
          return;
        }
        consecutiveErrors = 0;
        const p = getProgress(job);
        setProgress({ status: job.status || 'running', ...p, _job: job });
        if (TERMINAL.has(job.status)) {
          stopPolling();
          if (SUCCESS_TERMINAL.has(job.status)) onComplete?.(job);
          else onError?.(job);
          // Dwell on final state for 6s so user sees the counts.
          setTimeout(() => {
            if (mountedRef.current) setProgress(null);
          }, 6000);
        }
      } catch (err) {
        consecutiveErrors += 1;
        console.warn(`LiveJobButton poll error ${consecutiveErrors}/${POLL_ERROR_LIMIT} for ${statusEndpoint(jobId)}:`, err);
        if (consecutiveErrors >= POLL_ERROR_LIMIT) {
          stopPolling();
          if (mountedRef.current) setProgress(null);
        }
      }
    }, POLL_MS);
    safetyRef.current = setTimeout(() => stopPolling(), MAX_POLL_MS);
  }, [apiCall, token, statusEndpoint, getProgress, onComplete, onError, stopPolling]);

  // Rehydrate a running job on mount (e.g. user refreshed the page mid-run).
  useEffect(() => {
    mountedRef.current = true;
    if (rehydrateEndpoint) {
      (async () => {
        try {
          const res = await apiCall(rehydrateEndpoint, 'GET', null, token);
          const job = rehydratePickJob(res);
          if (job?.job_id && !TERMINAL.has(job.status) && mountedRef.current) {
            const p = getProgress(job);
            setProgress({ status: 'running', ...p, _job: job });
            poll(job.job_id);
          }
        } catch {
          // best-effort rehydration
        }
      })();
    }
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, []);  // eslint-disable-line

  const handleClick = async () => {
    if (dispatching || progress?.status === 'running') return;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setDispatching(true);
    try {
      const res = await apiCall(dispatchEndpoint, 'POST', dispatchPayload, token);
      const data = res?.data?.data || res?.data || res;
      const jobId = data?.job_id;
      if (jobId) {
        const p = getProgress(data);
        setProgress({ status: data.status || 'running', ...p, _job: data });
        poll(jobId);
      } else if (onComplete) {
        // Synchronous completion (no job_id returned)
        onComplete(data);
      }
    } catch (err) {
      console.error(`LiveJobButton dispatch failed:`, err);
      onError?.(err);
    } finally {
      setDispatching(false);
    }
  };

  const isRunning = progress?.status === 'running';
  const isDone = progress?.status && SUCCESS_TERMINAL.has(progress.status);
  const isFailed = progress?.status === 'failed';
  const disabled = parentDisabled || dispatching || isRunning;

  const runningIcon = icons.running || <RefreshCw className="w-4 h-4 animate-spin shrink-0" />;
  const doneIcon = icons.done || <CheckCircle className="w-4 h-4 mr-2 text-green-600" />;
  const failedIcon = icons.failed || <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />;
  const idleIcon = icons.idle;

  const renderRunning = () => {
    const pct = progress?.total
      ? Math.min(100, (progress.processed / progress.total) * 100)
      : null; // indeterminate
    const runningText = labels.running
      ? labels.running(progress)
      : progress?.total
        ? `SYNCING ${progress.processed} / ${progress.total}`
        : `RUNNING · ${progress.processed || 0} processed`;
    return (
      <span className="flex items-center gap-2 w-full">
        {runningIcon}
        <span className="flex flex-col items-start leading-tight flex-1 min-w-0">
          <span className="text-[11px] uppercase tracking-wide font-semibold truncate max-w-full">
            {runningText}
          </span>
          <span className={`relative w-full h-1 rounded-full overflow-hidden mt-0.5 ${progressTrackClassName}`}>
            <span
              className={`absolute top-0 h-full transition-all duration-500 ${progressBarClassName} ${
                pct === null ? 'animate-pulse left-0 w-1/3' : 'left-0'
              }`}
              style={pct !== null ? { width: `${pct}%` } : undefined}
            />
          </span>
        </span>
      </span>
    );
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      variant={variant}
      className={`min-w-[200px] ${className}`}
      data-testid={dataTestId}
    >
      {isRunning ? renderRunning()
       : isDone ? (
          <>
            {doneIcon}
            {labels.done ? labels.done(progress._job) : 'Done'}
          </>
        )
       : isFailed ? (
          <>
            {failedIcon}
            {labels.failed ? labels.failed(progress._job) : 'Failed'}
          </>
        )
       : (
          <>
            {idleIcon}
            {labels.idle || 'Run'}
          </>
        )}
    </Button>
  );
}
